import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { productionPacksAPI, productionAudioAPI, productionVisualsAPI, overviewVoiceAPI, editorNotesAPI, customIPPacksAPI } from '../lib/api';
import { chunkedUpload, shouldUseChunkedUpload } from '../lib/chunkedUpload';
import { getCategoryStyle, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Save,
  CheckCircle,
  FileText,
  Image,
  Music,
  MessageSquare,
  Target,
  StickyNote,
  Upload,
  ExternalLink,
  Trash2,
  Download,
  Play,
  Pause,
  FileAudio,
  User,
  Clock,
  Film,
  ImageIcon,
  Loader2,
  X,
  Mic,
  Square,
  RotateCcw,
  Instagram,
  Plus,
  Pencil,
  Package,
  Video,
  Check,
  Sparkles,
  Copy,
  Eye,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

// Helper functions
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Overview Voice Note Component
const OverviewVoiceNote = ({ packId, voiceNote, onUpdate }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [duration, setDuration] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const previewAudioRef = useRef(null);

  // Build stream URL for saved voice note
  const getStreamUrl = () => {
    const token = localStorage.getItem('token');
    return `${overviewVoiceAPI.getStreamUrl(packId)}?token=${token}`;
  };

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        setIsPreviewing(true);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  // Cancel recording/preview
  const cancelRecording = () => {
    setRecordedBlob(null);
    setIsPreviewing(false);
    setRecordingTime(0);
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
    }
  };

  // Confirm and upload
  const confirmRecording = async () => {
    if (!recordedBlob) return;
    
    setUploading(true);
    try {
      await overviewVoiceAPI.upload(packId, recordedBlob);
      toast.success('Voice note saved!');
      setRecordedBlob(null);
      setIsPreviewing(false);
      setRecordingTime(0);
      onUpdate();
    } catch (err) {
      console.error('Error uploading voice note:', err);
      toast.error('Failed to save voice note');
    } finally {
      setUploading(false);
    }
  };

  // Delete voice note
  const deleteVoiceNote = async () => {
    setDeleting(true);
    try {
      await overviewVoiceAPI.delete(packId);
      toast.success('Voice note deleted');
      onUpdate();
    } catch (err) {
      console.error('Error deleting voice note:', err);
      toast.error('Failed to delete voice note');
    } finally {
      setDeleting(false);
    }
  };

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Toggle playback of saved voice note
  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(err => {
          console.error('Playback error:', err);
          toast.error('Failed to play voice note');
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle playback events
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setPlaybackTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setPlaybackTime(0);
  };

  const handleSeek = (e) => {
    if (audioRef.current && duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      audioRef.current.currentTime = percent * duration;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isRecording]);

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 mb-6" data-testid="overview-voice-note">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
          <Mic className="w-4 h-4 text-[#D4AF37]" />
        </div>
        <h3 className="text-sm font-semibold text-zinc-800">Overview Voice Note</h3>
      </div>

      {/* Saved Voice Note */}
      {voiceNote && !isPreviewing && (
        <div className="bg-white rounded-lg p-4 border border-amber-100">
          <audio
            ref={audioRef}
            src={getStreamUrl()}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={handleEnded}
            crossOrigin="anonymous"
          />
          
          <div className="flex items-center gap-4">
            <button
              onClick={togglePlayback}
              className="w-10 h-10 flex items-center justify-center bg-[#D4AF37] text-white rounded-full hover:bg-amber-600 transition-colors flex-shrink-0"
              data-testid="play-overview-voice"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </button>
            
            <div className="flex-1 min-w-0">
              <div 
                className="h-2 bg-amber-100 rounded-full cursor-pointer overflow-hidden"
                onClick={handleSeek}
              >
                <div 
                  className="h-full bg-[#D4AF37] transition-all duration-100"
                  style={{ width: duration ? `${(playbackTime / duration) * 100}%` : '0%' }}
                />
              </div>
              <div className="flex justify-between text-xs text-zinc-500 mt-1">
                <span>{formatTime(playbackTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              asChild
              className="text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100"
              title="Download voice note"
            >
              <a
                href={getStreamUrl()}
                download={`overview-voice-note.webm`}
                data-testid="download-overview-voice"
              >
                <Download className="w-4 h-4" />
              </a>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={deleteVoiceNote}
              disabled={deleting}
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
              data-testid="delete-overview-voice"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </Button>
          </div>

          <div className="flex items-center gap-3 mt-3 text-xs text-zinc-500">
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {voiceNote.recorder_name}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDateTime(voiceNote.created_at)}
            </span>
          </div>
        </div>
      )}

      {/* Recording/Preview State */}
      {isPreviewing && recordedBlob && (
        <div className="bg-white rounded-lg p-4 border border-amber-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-2 h-2 bg-amber-500 rounded-full" />
            <span className="text-sm font-medium text-zinc-700">Preview Recording ({formatTime(recordingTime)})</span>
          </div>
          
          <audio 
            ref={previewAudioRef}
            src={URL.createObjectURL(recordedBlob)} 
            controls 
            className="w-full h-10 mb-3"
          />
          
          <div className="flex gap-2">
            <Button
              onClick={confirmRecording}
              disabled={uploading}
              className="flex-1 bg-[#D4AF37] hover:bg-amber-600 text-white"
              data-testid="confirm-voice-note"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirm & Save
                </>
              )}
            </Button>
            <Button
              onClick={cancelRecording}
              variant="outline"
              className="flex-1"
              disabled={uploading}
              data-testid="cancel-voice-note"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Re-record
            </Button>
          </div>
        </div>
      )}

      {/* Recording State */}
      {isRecording && (
        <div className="bg-white rounded-lg p-4 border border-red-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-zinc-700">Recording...</span>
            </div>
            <span className="text-lg font-mono text-red-600">{formatTime(recordingTime)}</span>
          </div>
          
          <Button
            onClick={stopRecording}
            className="w-full bg-red-500 hover:bg-red-600 text-white"
            data-testid="stop-recording"
          >
            <Square className="w-4 h-4 mr-2" />
            Stop Recording
          </Button>
        </div>
      )}

      {/* Record Button (when no recording/preview) */}
      {!voiceNote && !isRecording && !isPreviewing && (
        <Button
          onClick={startRecording}
          variant="outline"
          className="w-full border-dashed border-2 border-amber-300 hover:border-[#D4AF37] hover:bg-amber-50 h-14"
          data-testid="start-recording"
        >
          <Mic className="w-5 h-5 mr-2 text-[#D4AF37]" />
          <span className="text-zinc-600">Click to Record Overview</span>
        </Button>
      )}

      {/* Re-record button when voice note exists */}
      {voiceNote && !isPreviewing && !isRecording && (
        <Button
          onClick={startRecording}
          variant="ghost"
          size="sm"
          className="mt-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
          data-testid="rerecord-voice-note"
        >
          <RotateCcw className="w-3 h-3 mr-1" />
          Record New
        </Button>
      )}
    </div>
  );
};

// Editor Voice Note Recorder Component (for multiple voice notes)
const EditorVoiceNoteRecorder = ({ packId, onUploadComplete }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploading, setUploading] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const previewAudioRef = useRef(null);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        setIsPreviewing(true);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const cancelRecording = () => {
    setRecordedBlob(null);
    setIsPreviewing(false);
    setRecordingTime(0);
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
    }
  };

  const confirmRecording = async () => {
    if (!recordedBlob) return;
    
    setUploading(true);
    try {
      await editorNotesAPI.uploadVoice(packId, recordedBlob);
      toast.success('Voice note added!');
      setRecordedBlob(null);
      setIsPreviewing(false);
      setRecordingTime(0);
      onUploadComplete();
    } catch (err) {
      console.error('Error uploading voice note:', err);
      toast.error('Failed to save voice note');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isRecording]);

  if (isPreviewing && recordedBlob) {
    return (
      <div className="bg-zinc-50 rounded-lg p-4 border border-zinc-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-2 h-2 bg-amber-500 rounded-full" />
          <span className="text-sm font-medium text-zinc-700">Preview Recording ({formatTime(recordingTime)})</span>
        </div>
        
        <audio 
          ref={previewAudioRef}
          src={URL.createObjectURL(recordedBlob)} 
          controls 
          className="w-full h-10 mb-3"
        />
        
        <div className="flex gap-2">
          <Button
            onClick={confirmRecording}
            disabled={uploading}
            size="sm"
            className="flex-1 bg-[#D4AF37] hover:bg-amber-600 text-white"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
            {uploading ? 'Saving...' : 'Save'}
          </Button>
          <Button onClick={cancelRecording} variant="outline" size="sm" className="flex-1" disabled={uploading}>
            <RotateCcw className="w-4 h-4 mr-1" /> Re-record
          </Button>
        </div>
      </div>
    );
  }

  if (isRecording) {
    return (
      <div className="bg-red-50 rounded-lg p-4 border border-red-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-zinc-700">Recording...</span>
          </div>
          <span className="text-lg font-mono text-red-600">{formatTime(recordingTime)}</span>
        </div>
        <Button onClick={stopRecording} className="w-full bg-red-500 hover:bg-red-600 text-white" size="sm">
          <Square className="w-4 h-4 mr-2" /> Stop Recording
        </Button>
      </div>
    );
  }

  return (
    <Button
      onClick={startRecording}
      variant="outline"
      className="w-full border-dashed border-2 h-12 hover:border-[#D4AF37] hover:bg-amber-50"
    >
      <Mic className="w-4 h-4 mr-2 text-[#D4AF37]" />
      <span className="text-zinc-600">Record Voice Note</span>
    </Button>
  );
};

// Editor Voice Note Player (for playing saved notes)
const EditorVoiceNotePlayer = ({ packId, voiceNote, onDelete }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const audioRef = useRef(null);

  const getStreamUrl = () => {
    const token = localStorage.getItem('token');
    return `${editorNotesAPI.getVoiceStreamUrl(packId, voiceNote.id)}?token=${token}`;
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(err => {
          console.error('Playback error:', err);
          toast.error('Failed to play voice note');
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await editorNotesAPI.deleteVoice(packId, voiceNote.id);
      toast.success('Voice note deleted');
      onDelete();
    } catch (err) {
      toast.error('Failed to delete voice note');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-3">
      <audio
        ref={audioRef}
        src={getStreamUrl()}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onEnded={() => { setIsPlaying(false); setCurrentTime(0); }}
        crossOrigin="anonymous"
      />
      
      <div className="flex items-center gap-3">
        <button
          onClick={togglePlay}
          className="w-8 h-8 flex items-center justify-center bg-[#D4AF37] text-white rounded-full hover:bg-amber-600 transition-colors flex-shrink-0"
        >
          {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3 ml-0.5" />}
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="h-1.5 bg-zinc-200 rounded-full cursor-pointer overflow-hidden"
               onClick={(e) => {
                 if (audioRef.current && duration) {
                   const rect = e.currentTarget.getBoundingClientRect();
                   const percent = (e.clientX - rect.left) / rect.width;
                   audioRef.current.currentTime = percent * duration;
                 }
               }}>
            <div className="h-full bg-[#D4AF37] transition-all duration-100"
                 style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }} />
          </div>
          <div className="flex justify-between items-center mt-1">
            <span className="text-xs text-zinc-400">{formatTime(currentTime)} / {formatTime(duration)}</span>
            <span className="text-xs text-zinc-500 flex items-center gap-1">
              <User className="w-3 h-3" /> {voiceNote.recorder_name}
            </span>
          </div>
        </div>

        <Button variant="ghost" size="sm" asChild
                className="text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 p-1 h-auto"
                title="Download voice note">
          <a
            href={getStreamUrl()}
            download={`voice-note-${voiceNote.id}.webm`}
            data-testid={`download-voice-note-${voiceNote.id}`}
          >
            <Download className="w-4 h-4" />
          </a>
        </Button>

        <Button variant="ghost" size="sm" onClick={handleDelete} disabled={deleting}
                className="text-red-500 hover:text-red-600 hover:bg-red-50 p-1 h-auto">
          {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
        </Button>
      </div>
      
      <p className="text-xs text-zinc-400 mt-2 flex items-center gap-1">
        <Clock className="w-3 h-3" />
        {formatDateTime(voiceNote.created_at)}
      </p>
    </div>
  );
};

// Instagram Reference Component
const InstagramReference = ({ packId, reference, onDelete }) => {
  const [deleting, setDeleting] = useState(false);

  // Extract Instagram post ID from URL for embedding
  const getEmbedUrl = (url) => {
    // Convert instagram.com/reel/xxx or instagram.com/p/xxx to embed URL
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      // Return the URL with /embed/ appended
      return `https://www.instagram.com${pathname}embed/`;
    } catch {
      return null;
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await editorNotesAPI.deleteInstagram(packId, reference.id);
      toast.success('Reference deleted');
      onDelete();
    } catch (err) {
      toast.error('Failed to delete reference');
    } finally {
      setDeleting(false);
    }
  };

  const embedUrl = getEmbedUrl(reference.url);

  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
      {/* Instagram Embed */}
      <div className="aspect-[9/16] max-h-[400px] bg-zinc-100 relative">
        {embedUrl ? (
          <iframe
            src={embedUrl}
            className="w-full h-full border-0"
            allowFullScreen
            scrolling="no"
            title="Instagram Post"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <a href={reference.url} target="_blank" rel="noopener noreferrer" 
               className="text-[#D4AF37] hover:underline flex items-center gap-2">
              <Instagram className="w-5 h-5" />
              Open in Instagram
            </a>
          </div>
        )}
      </div>
      
      {/* Actions */}
      <div className="p-3 border-t border-zinc-100">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <a href={reference.url} target="_blank" rel="noopener noreferrer"
               className="text-sm text-[#D4AF37] hover:underline truncate block">
              <ExternalLink className="w-3 h-3 inline mr-1" />
              Open Original
            </a>
            <p className="text-xs text-zinc-400 mt-1 flex items-center gap-1">
              <User className="w-3 h-3" /> {reference.adder_name}
              <span className="mx-1">•</span>
              <Clock className="w-3 h-3" /> {formatDateTime(reference.created_at)}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleDelete} disabled={deleting}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50">
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Add Instagram Reference Input
const AddInstagramReference = ({ packId, onAdd, disabled }) => {
  const [url, setUrl] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!url.trim()) {
      toast.error('Please enter an Instagram URL');
      return;
    }
    
    if (!url.includes('instagram.com') && !url.includes('instagr.am')) {
      toast.error('Please enter a valid Instagram URL');
      return;
    }

    setAdding(true);
    try {
      await editorNotesAPI.addInstagram(packId, url);
      toast.success('Reference added!');
      setUrl('');
      onAdd();
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to add reference';
      toast.error(message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="flex gap-2">
      <Input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="Paste Instagram reel/post URL..."
        className="flex-1"
        disabled={disabled || adding}
        onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
      />
      <Button onClick={handleAdd} disabled={disabled || adding || !url.trim()}
              className="bg-[#D4AF37] hover:bg-amber-600 text-white">
        {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
      </Button>
    </div>
  );
};

// Audio Player Component
const AudioPlayer = ({ packId, audioId }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const url = `${productionAudioAPI.getStreamUrl(packId, audioId)}?token=${token}`;
    setAudioUrl(url);
  }, [packId, audioId]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(err => {
          console.error('Failed to play audio:', err);
          toast.error('Failed to play audio');
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e) => {
    if (audioRef.current && duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      audioRef.current.currentTime = percent * duration;
    }
  };

  if (!audioUrl) return null;

  return (
    <div className="flex items-center gap-3 flex-1">
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        crossOrigin="anonymous"
      />
      <button
        onClick={togglePlay}
        className="w-8 h-8 flex items-center justify-center bg-zinc-900 text-white rounded-full hover:bg-zinc-700 transition-colors flex-shrink-0"
        data-testid={`play-btn-${audioId}`}
      >
        {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div 
          className="h-1.5 bg-zinc-200 rounded-full cursor-pointer overflow-hidden"
          onClick={handleSeek}
        >
          <div 
            className="h-full bg-[#D4AF37] transition-all duration-100"
            style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
          />
        </div>
        <div className="flex justify-between text-xs text-zinc-400 mt-1">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
};

// Visual Item Component - handles both images and videos with proper previews
const VisualItem = ({ packId, visual, onDelete, canDelete }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const url = `${productionVisualsAPI.getFileUrl(packId, visual.id)}?token=${token}`;
    setImageUrl(url);
    setLoading(false);
  }, [packId, visual.id]);

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(productionVisualsAPI.getDownloadUrl(packId, visual.id), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        console.error('Download response not ok:', response.status, response.statusText);
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = visual.original_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Download started!');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download file');
    }
  };

  // Direct download as fallback
  const handleDirectDownload = () => {
    const token = localStorage.getItem('token');
    const downloadUrl = `${productionVisualsAPI.getDownloadUrl(packId, visual.id)}?token=${token}`;
    window.open(downloadUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="relative aspect-square bg-zinc-100 rounded-lg flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-zinc-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="group relative bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid={`visual-item-${visual.id}`}>
      {/* Media Preview */}
      <div className="aspect-square bg-zinc-100 relative">
        {visual.is_video ? (
          videoError ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-100 p-4">
              <Film className="w-8 h-8 text-zinc-400 mb-2" />
              <p className="text-xs text-zinc-500 text-center mb-2">Video preview unavailable</p>
              <Button size="sm" variant="outline" onClick={handleDirectDownload}>
                <Download className="w-3 h-3 mr-1" /> Download to view
              </Button>
            </div>
          ) : (
            <video 
              src={imageUrl}
              className="w-full h-full object-cover"
              controls
              preload="auto"
              playsInline
              crossOrigin="anonymous"
              onError={(e) => {
                console.error('Video load error:', e);
                setVideoError(true);
              }}
              onLoadStart={() => setVideoError(false)}
            />
          )
        ) : (
          error ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-100 p-4">
              <ImageIcon className="w-8 h-8 text-zinc-400 mb-2" />
              <p className="text-xs text-zinc-500 text-center mb-2">Preview unavailable</p>
              <Button size="sm" variant="outline" onClick={handleDirectDownload}>
                <Download className="w-3 h-3 mr-1" /> Download
              </Button>
            </div>
          ) : (
            <img 
              src={imageUrl}
              alt={visual.original_name}
              className="w-full h-full object-cover"
              onError={() => setError(true)}
            />
          )
        )}
        
        {/* Type Badge */}
        <div className="absolute top-2 left-2">
          <span className={`text-xs px-2 py-1 rounded-full ${visual.is_video ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
            {visual.is_video ? <Film className="w-3 h-3 inline mr-1" /> : <ImageIcon className="w-3 h-3 inline mr-1" />}
            {visual.is_video ? 'Video' : 'Image'}
          </span>
        </div>

        {/* Action Buttons - For videos: always visible in corner, For images: overlay on hover */}
        {visual.is_video ? (
          /* Video: Show buttons in top-right corner, always visible on hover but not blocking player */
          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleDownload}
              className="bg-white/90 hover:bg-white h-8 w-8 p-0"
              data-testid={`download-visual-${visual.id}`}
            >
              <Download className="w-4 h-4" />
            </Button>
            {canDelete && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => onDelete(visual.id)}
                className="h-8 w-8 p-0"
                data-testid={`delete-visual-${visual.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        ) : (
          /* Image: Full overlay with centered buttons */
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleDownload}
              className="bg-white/90 hover:bg-white"
              data-testid={`download-visual-${visual.id}`}
            >
              <Download className="w-4 h-4" />
            </Button>
            {canDelete && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => onDelete(visual.id)}
                data-testid={`delete-visual-${visual.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* File Info */}
      <div className="p-3">
        <p className="text-sm font-medium text-zinc-900 truncate" title={visual.original_name}>
          {visual.original_name}
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-zinc-500">
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {visual.uploader_name}
          </span>
          <span>{formatFileSize(visual.file_size)}</span>
        </div>
        <p className="text-xs text-zinc-400 mt-1">
          <Clock className="w-3 h-3 inline mr-1" />
          {formatDateTime(visual.created_at)}
        </p>
      </div>
    </div>
  );
};

// Visual Upload Component - handles multiple file uploads with progress
const VisualUploader = ({ packId, visualType, onUploadComplete, disabled }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const inputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    setUploadQueue(files.map(f => ({ name: f.name, status: 'pending' })));
    setCurrentIndex(0);

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < files.length; i++) {
      setCurrentIndex(i);
      setUploadQueue(prev => prev.map((item, idx) => 
        idx === i ? { ...item, status: 'uploading' } : item
      ));

      try {
        if (shouldUseChunkedUpload(files[i])) {
          await chunkedUpload(
            files[i],
            'production_visual',
            packId,
            null,
            { visual_type: visualType }
          );
        } else {
          await productionVisualsAPI.upload(packId, files[i], visualType);
        }
        successCount++;
        setUploadQueue(prev => prev.map((item, idx) => 
          idx === i ? { ...item, status: 'success' } : item
        ));
      } catch (err) {
        console.error('Upload error:', err);
        failCount++;
        setUploadQueue(prev => prev.map((item, idx) => 
          idx === i ? { ...item, status: 'error' } : item
        ));
      }
    }

    // Show summary
    if (successCount > 0 && failCount === 0) {
      toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully!`);
    } else if (successCount > 0 && failCount > 0) {
      toast.warning(`${successCount} uploaded, ${failCount} failed`);
    } else if (failCount > 0) {
      toast.error(`Failed to upload ${failCount} file${failCount > 1 ? 's' : ''}`);
    }

    // Clear and refresh
    setTimeout(() => {
      setUploading(false);
      setUploadQueue([]);
      setCurrentIndex(0);
      if (inputRef.current) inputRef.current.value = '';
      onUploadComplete();
    }, 500);
  };

  return (
    <div className="space-y-3">
      {/* Upload Button */}
      <div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || uploading}
          data-testid={`visual-upload-input-${visualType}`}
        />
        <Button
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
          variant="outline"
          className="w-full border-dashed border-2 h-20 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-colors"
          data-testid={`visual-upload-btn-${visualType}`}
        >
          {uploading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Uploading {currentIndex + 1} of {uploadQueue.length}...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Upload className="w-6 h-6 text-zinc-400" />
              <span className="text-sm text-zinc-500">Click to upload images or videos</span>
              <span className="text-xs text-zinc-400">Supports multiple files</span>
            </div>
          )}
        </Button>
      </div>

      {/* Upload Progress */}
      {uploading && uploadQueue.length > 0 && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-zinc-700">Uploading files...</span>
            <span className="text-xs text-zinc-500">{currentIndex + 1}/{uploadQueue.length}</span>
          </div>
          <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#D4AF37] transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / uploadQueue.length) * 100}%` }}
            />
          </div>
          <div className="max-h-32 overflow-y-auto space-y-1 mt-2">
            {uploadQueue.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs">
                {item.status === 'pending' && <div className="w-3 h-3 rounded-full bg-zinc-300" />}
                {item.status === 'uploading' && <Loader2 className="w-3 h-3 text-[#D4AF37] animate-spin" />}
                {item.status === 'success' && <CheckCircle className="w-3 h-3 text-green-500" />}
                {item.status === 'error' && <X className="w-3 h-3 text-red-500" />}
                <span className={`truncate ${item.status === 'error' ? 'text-red-500' : 'text-zinc-600'}`}>
                  {item.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ====================== Custom IP — Founders Visuals helpers ======================

const formatFileSizeC = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

// Single Founders-Visual tile with proper <video controls> + image preview + download + delete
const FoundersVisualItem = ({ packId, item, onDelete, canDelete }) => {
  const [videoError, setVideoError] = useState(false);
  const [imageError, setImageError] = useState(false);
  const isVideo = item.is_video || (item.mime_type || '').startsWith('video/') || /\.(mp4|mov|avi|webm|mkv|m4v|mpeg)$/i.test(item.original_name || item.filename || '');

  const inlineUrl = customIPPacksAPI.getMediaUrl(packId, 'founders_visuals', item.id);
  const downloadUrl = customIPPacksAPI.getMediaDownloadUrl(packId, 'founders_visuals', item.id);

  const handleDownload = async () => {
    try {
      const res = await fetch(downloadUrl);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = item.original_name || item.filename || 'file';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Founders visual download error', err);
      // Fallback: open in new tab so browser handles it
      window.open(downloadUrl, '_blank');
    }
  };

  return (
    <div className="group relative bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid={`founders-visual-item-${item.id}`}>
      <div className="aspect-square bg-zinc-100 relative">
        {isVideo ? (
          videoError ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-100 p-4">
              <Film className="w-8 h-8 text-zinc-400 mb-2" />
              <p className="text-xs text-zinc-500 text-center mb-2">Video preview unavailable</p>
              <Button size="sm" variant="outline" onClick={handleDownload}>
                <Download className="w-3 h-3 mr-1" /> Download to view
              </Button>
            </div>
          ) : (
            <video
              src={inlineUrl}
              className="w-full h-full object-cover"
              controls
              preload="metadata"
              playsInline
              crossOrigin="anonymous"
              onError={(e) => { console.error('Founders video load error:', e); setVideoError(true); }}
            />
          )
        ) : (
          imageError ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-100 p-4">
              <ImageIcon className="w-8 h-8 text-zinc-400 mb-2" />
              <p className="text-xs text-zinc-500 text-center mb-2">Preview unavailable</p>
              <Button size="sm" variant="outline" onClick={handleDownload}>
                <Download className="w-3 h-3 mr-1" /> Download
              </Button>
            </div>
          ) : (
            <img
              src={inlineUrl}
              alt={item.original_name || ''}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          )
        )}

        <div className="absolute top-2 left-2">
          <span className={`text-xs px-2 py-1 rounded-full ${isVideo ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
            {isVideo ? <Film className="w-3 h-3 inline mr-1" /> : <ImageIcon className="w-3 h-3 inline mr-1" />}
            {isVideo ? 'Video' : 'Image'}
          </span>
        </div>

        {isVideo ? (
          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <Button size="sm" variant="secondary" onClick={handleDownload} className="bg-white/90 hover:bg-white h-8 w-8 p-0" data-testid={`founders-visual-download-${item.id}`}>
              <Download className="w-4 h-4" />
            </Button>
            {canDelete && (
              <Button size="sm" variant="destructive" onClick={() => onDelete(item.id)} className="h-8 w-8 p-0" data-testid={`founders-visual-delete-${item.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        ) : (
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button size="sm" variant="secondary" onClick={handleDownload} className="bg-white/90 hover:bg-white" data-testid={`founders-visual-download-${item.id}`}>
              <Download className="w-4 h-4" />
            </Button>
            {canDelete && (
              <Button size="sm" variant="destructive" onClick={() => onDelete(item.id)} data-testid={`founders-visual-delete-${item.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="p-3">
        <p className="text-sm font-medium text-zinc-900 truncate" title={item.original_name}>
          {item.original_name || item.filename}
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-zinc-500">
          <span>{formatFileSizeC(item.file_size)}</span>
          {item.uploaded_at && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(item.uploaded_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Multi-file uploader for Founders Visuals (chunks files >5MB automatically)
const FoundersVisualsUploader = ({ packId, onUploadComplete, disabled }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const inputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    setUploadQueue(files.map((f) => ({ name: f.name, status: 'pending' })));
    setCurrentIndex(0);

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < files.length; i++) {
      setCurrentIndex(i);
      setUploadQueue((prev) => prev.map((item, idx) => (idx === i ? { ...item, status: 'uploading' } : item)));
      try {
        if (shouldUseChunkedUpload(files[i])) {
          await chunkedUpload(files[i], 'custom_ip_media', packId, null, { section: 'founders_visuals' });
        } else {
          await customIPPacksAPI.uploadMedia(packId, 'founders_visuals', files[i]);
        }
        successCount++;
        setUploadQueue((prev) => prev.map((item, idx) => (idx === i ? { ...item, status: 'success' } : item)));
      } catch (err) {
        console.error('Founders visual upload error', err);
        failCount++;
        setUploadQueue((prev) => prev.map((item, idx) => (idx === i ? { ...item, status: 'error' } : item)));
      }
    }

    if (successCount > 0 && failCount === 0) toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded`);
    else if (successCount > 0 && failCount > 0) toast.warning(`${successCount} uploaded, ${failCount} failed`);
    else if (failCount > 0) toast.error(`Failed to upload ${failCount} file${failCount > 1 ? 's' : ''}`);

    setTimeout(() => {
      setUploading(false);
      setUploadQueue([]);
      setCurrentIndex(0);
      if (inputRef.current) inputRef.current.value = '';
      onUploadComplete?.();
    }, 500);
  };

  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || uploading}
        data-testid="founders-visuals-upload-input"
      />
      <Button
        onClick={() => inputRef.current?.click()}
        disabled={disabled || uploading}
        variant="outline"
        className="w-full border-dashed border-2 h-20 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-colors"
        data-testid="founders-visuals-upload-btn"
      >
        {uploading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Uploading {currentIndex + 1} of {uploadQueue.length}...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1">
            <Upload className="w-6 h-6 text-zinc-400" />
            <span className="text-sm text-zinc-500">Click to upload images or videos</span>
            <span className="text-xs text-zinc-400">Supports multiple files · large videos use chunked upload</span>
          </div>
        )}
      </Button>

      {uploading && uploadQueue.length > 0 && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-zinc-700">Uploading files...</span>
            <span className="text-xs text-zinc-500">{currentIndex + 1}/{uploadQueue.length}</span>
          </div>
          <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
            <div className="h-full bg-[#D4AF37] transition-all duration-300" style={{ width: `${((currentIndex + 1) / uploadQueue.length) * 100}%` }} />
          </div>
          <div className="max-h-32 overflow-y-auto space-y-1 mt-2">
            {uploadQueue.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs">
                {item.status === 'pending' && <div className="w-3 h-3 rounded-full bg-zinc-300" />}
                {item.status === 'uploading' && <Loader2 className="w-3 h-3 text-[#D4AF37] animate-spin" />}
                {item.status === 'success' && <CheckCircle className="w-3 h-3 text-green-500" />}
                {item.status === 'error' && <X className="w-3 h-3 text-red-500" />}
                <span className={`truncate ${item.status === 'error' ? 'text-red-500' : 'text-zinc-600'}`}>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ProductionPackDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user, isFounder } = useAuth();
  const isFounderUser = isFounder();
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [audioDeleteId, setAudioDeleteId] = useState(null);
  const [visualDeleteId, setVisualDeleteId] = useState(null);
  const audioInputRef = useRef(null);
  const [formData, setFormData] = useState({
    hook: '',
    script: '',
    scenes: '',
    audio_link: '',
    captions: [],
    cta: '',
    notes: '',
  });
  const [newCaption, setNewCaption] = useState('');
  const [generatingCaption, setGeneratingCaption] = useState(false);
  const [captionOptions, setCaptionOptions] = useState(null);
  const [uploadingFinalAsset, setUploadingFinalAsset] = useState(false);
  const [finalAssetProgress, setFinalAssetProgress] = useState(null); // { name, index, total, percent }
  const [previewFinalAsset, setPreviewFinalAsset] = useState(null);
  const finalAssetInputRef = useRef(null);
  const [customIPData, setCustomIPData] = useState(null);
  const [customIPUploading, setCustomIPUploading] = useState(null);
  const [customIPScript, setCustomIPScript] = useState('');
  const [savingCustomIPScript, setSavingCustomIPScript] = useState(false);
  const [customIPReelUrl, setCustomIPReelUrl] = useState('');
  const [addingCustomIPReel, setAddingCustomIPReel] = useState(false);
  const customIPFileRefs = useRef({});

  useEffect(() => {
    fetchPack();
  }, [id]);

  const fetchPack = async () => {
    try {
      initialLoad.current = true;
      const response = await productionPacksAPI.getById(id);
      setPack(response.data);
      setFormData({
        hook: response.data.hook || '',
        script: response.data.script || '',
        scenes: response.data.scenes || '',
        audio_link: response.data.audio_link || '',
        captions: response.data.captions || [],
        cta: response.data.cta || '',
        notes: response.data.notes || '',
      });
      // Fetch Custom IP data if this is a Custom IP production pack
      if (response.data.custom_ip_pack_id) {
        fetchCustomIPData(response.data.custom_ip_pack_id);
      }
    } catch (error) {
      toast.error('Failed to fetch production pack');
      navigate('/production-packs');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomIPData = async (customIPPackId) => {
    try {
      const response = await customIPPacksAPI.getById(customIPPackId);
      setCustomIPData(response.data);
      setCustomIPScript(response.data.script || '');
    } catch (error) {
      console.error('Failed to fetch Custom IP data:', error);
    }
  };

  const handleCustomIPUpload = async (section, file) => {
    if (!customIPData) return;
    setCustomIPUploading(section);
    try {
      if (shouldUseChunkedUpload(file)) {
        await chunkedUpload(file, 'custom_ip_media', customIPData.id, null, { section });
      } else {
        await customIPPacksAPI.uploadMedia(customIPData.id, section, file);
      }
      toast.success('Media uploaded');
      fetchCustomIPData(customIPData.id);
    } catch (error) {
      console.error('Custom IP upload error:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setCustomIPUploading(null);
    }
  };

  const handleCustomIPDeleteMedia = async (section, mediaId) => {
    if (!customIPData) return;
    try {
      await customIPPacksAPI.deleteMedia(customIPData.id, section, mediaId);
      toast.success('Media deleted');
      fetchCustomIPData(customIPData.id);
    } catch {
      toast.error('Failed to delete media');
    }
  };

  const handleSaveCustomIPScript = async () => {
    if (!customIPData) return;
    setSavingCustomIPScript(true);
    try {
      await customIPPacksAPI.update(customIPData.id, { script: customIPScript });
      toast.success('Script saved');
      fetchCustomIPData(customIPData.id);
    } catch {
      toast.error('Failed to save script');
    } finally {
      setSavingCustomIPScript(false);
    }
  };

  const handleAddCustomIPReel = async () => {
    if (!customIPData || !customIPReelUrl.trim()) { toast.error('Enter an Instagram URL'); return; }
    setAddingCustomIPReel(true);
    try {
      await customIPPacksAPI.addReferenceReel(customIPData.id, customIPReelUrl.trim());
      toast.success('Reference reel added');
      setCustomIPReelUrl('');
      fetchCustomIPData(customIPData.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add reel');
    } finally {
      setAddingCustomIPReel(false);
    }
  };

  const handleDeleteCustomIPReel = async (refId) => {
    if (!customIPData) return;
    try {
      await customIPPacksAPI.deleteReferenceReel(customIPData.id, refId);
      toast.success('Reference reel removed');
      fetchCustomIPData(customIPData.id);
    } catch {
      toast.error('Failed to delete reel');
    }
  };

  const handleSave = async (data) => {
    const toSave = data || formData;
    setSaving(true);
    try {
      await productionPacksAPI.update(id, toSave);
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Auto-save: debounce 1.2s after any formData change
  const autoSaveTimer = useRef(null);
  const initialLoad = useRef(true);

  useEffect(() => {
    // Skip auto-save on initial load (when formData is set from fetch)
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      handleSave(formData);
    }, 1200);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [formData]);

  const handleApprove = async () => {
    try {
      await productionPacksAPI.approve(id);
      toast.success('Production pack approved!');
      fetchPack();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleMarkCompleted = async () => {
    try {
      await productionPacksAPI.markCompleted(id);
      toast.success('Production pack marked as completed!');
      fetchPack();
    } catch (error) {
      toast.error('Failed to mark as completed');
    }
  };

  // Audio upload handler
  const handleAudioUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/aac', 'audio/m4a', 'audio/x-m4a', 'audio/mp4', 'audio/webm', 'audio/flac'];
    const allowedExtensions = ['.mp3', '.wav', '.ogg', '.aac', '.m4a', '.mp4', '.webm', '.flac'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExt)) {
      toast.error('Invalid file type. Please upload an audio file (MP3, WAV, OGG, AAC, M4A, FLAC)');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 50MB.');
      return;
    }

    setUploadingAudio(true);
    try {
      await productionAudioAPI.upload(id, file);
      toast.success('Audio uploaded successfully!');
      fetchPack();
    } catch (error) {
      toast.error('Failed to upload audio file');
    } finally {
      setUploadingAudio(false);
      if (audioInputRef.current) audioInputRef.current.value = '';
    }
  };

  const handleAudioDownload = async (audio) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(productionAudioAPI.getDownloadUrl(id, audio.id), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = audio.original_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Download started!');
    } catch (error) {
      toast.error('Failed to download audio file');
    }
  };

  const handleAudioDelete = async () => {
    if (!audioDeleteId) return;
    try {
      await productionAudioAPI.delete(id, audioDeleteId);
      toast.success('Audio deleted successfully!');
      setAudioDeleteId(null);
      fetchPack();
    } catch (error) {
      toast.error('Failed to delete audio file');
    }
  };

  const handleVisualDelete = async () => {
    if (!visualDeleteId) return;
    try {
      await productionVisualsAPI.delete(id, visualDeleteId);
      toast.success('Visual deleted successfully!');
      setVisualDeleteId(null);
      fetchPack();
    } catch (error) {
      toast.error('Failed to delete visual file');
    }
  };

  const canDeleteFile = () => {
    return true;
  };

  const addCaption = () => {
    if (!newCaption.trim()) return;
    setFormData(prev => ({
      ...prev,
      captions: [...prev.captions, newCaption]
    }));
    setNewCaption('');
  };

  const removeCaption = (index) => {
    setFormData(prev => ({
      ...prev,
      captions: prev.captions.filter((_, i) => i !== index)
    }));
  };

  const parseCaptionOptions = (text) => {
    if (!text) return [];
    const headerFinder = /\*{0,2}\s*OPTION\s+([A-C])\s*[:\s–—-]*\s*(.*?)(?:\*{0,2})\s*(?:\n|$)/gi;
    const headers = [];
    let m;
    while ((m = headerFinder.exec(text)) !== null) {
      headers.push({
        label: `Option ${m[1].toUpperCase()}`,
        subtitle: m[2]?.replace(/\*+/g, '').replace(/^[:\s–—-]+|[:\s–—-]+$/g, '').trim() || '',
        start: m.index,
        headerEnd: m.index + m[0].length,
      });
    }
    if (headers.length === 0) return [{ label: 'Caption', subtitle: '', content: text.trim() }];
    return headers.map((h, i) => {
      const contentEnd = i < headers.length - 1 ? headers[i + 1].start : text.length;
      return { label: h.label, subtitle: h.subtitle, content: text.slice(h.headerEnd, contentEnd).trim() };
    });
  };

  const handleGenerateCaption = async () => {
    setGeneratingCaption(true);
    setCaptionOptions(null);
    try {
      const res = await productionPacksAPI.generateCaption(pack.id);
      const options = parseCaptionOptions(res.data.generated_captions);
      setCaptionOptions(options);
      toast.success('Captions generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate captions');
    } finally {
      setGeneratingCaption(false);
    }
  };

  const addGeneratedCaption = (text) => {
    setFormData(prev => ({
      ...prev,
      captions: [...prev.captions, text]
    }));
    toast.success('Caption added');
  };

  // Final Assets handlers
  const handleFinalAssetUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingFinalAsset(true);
    let successCount = 0;
    let failCount = 0;
    let lastError = '';
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setFinalAssetProgress({ name: file.name, index: i + 1, total: files.length, percent: 0 });
      try {
        if (shouldUseChunkedUpload(file)) {
          await chunkedUpload(file, 'production_final_asset', id, (percent) => {
            setFinalAssetProgress({ name: file.name, index: i + 1, total: files.length, percent });
          });
        } else {
          await productionPacksAPI.uploadFinalAsset(id, file);
          setFinalAssetProgress({ name: file.name, index: i + 1, total: files.length, percent: 100 });
        }
        successCount++;
      } catch (err) {
        console.error('Final asset upload failed:', err);
        failCount++;
        lastError =
          err.response?.data?.detail ||
          err.message ||
          'Upload failed (network or server error)';
      }
    }
    if (successCount > 0 && failCount === 0) {
      toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded`);
    } else if (successCount > 0 && failCount > 0) {
      toast.warning(
        `${successCount} uploaded, ${failCount} failed — ${lastError}`
      );
    } else if (failCount > 0) {
      toast.error(
        `Upload failed (${failCount} file${failCount > 1 ? 's' : ''}) — ${lastError}`
      );
    }
    setUploadingFinalAsset(false);
    setFinalAssetProgress(null);
    if (finalAssetInputRef.current) finalAssetInputRef.current.value = '';
    fetchPack();
  };

  const handleDeleteFinalAsset = async (mediaId) => {
    try {
      await productionPacksAPI.deleteFinalAsset(id, mediaId);
      toast.success('File deleted');
      fetchPack();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDownloadFinalAsset = (mediaId, name) => {
    const url = productionPacksAPI.getFinalAssetDownloadUrl(id, mediaId);
    const a = document.createElement('a');
    a.href = url;
    a.download = name || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6" data-testid="production-pack-detail-loading">
        <div className="h-8 w-48 bg-zinc-100 rounded" />
        <div className="h-96 bg-zinc-100 rounded" />
      </div>
    );
  }

  if (!pack) return null;

  const categoryStyle = getCategoryStyle(pack.category);
  const rawVisualFiles = pack.raw_visual_files || [];
  const referenceVisualFiles = pack.reference_visual_files || [];
  const audioFiles = pack.audio_files || [];
  const editorInstagramRefs = pack.editor_instagram_refs || [];
  const editorVoiceNotes = pack.editor_voice_notes || [];
  const isCustomIP = !!pack.custom_ip_pack_id;

  const CUSTOM_IP_SECTIONS = [
    { key: 'customer_reference', label: 'Customer Reference', description: 'Customer photos and reference images' },
    { key: 'cad_quote', label: 'CAD / Quote', description: 'CAD designs and price quotes' },
    { key: 'final_product', label: 'Final Product', description: 'Final product photos and videos' },
    { key: 'reviews', label: 'Reviews', description: 'Customer reviews and testimonials' },
  ];

  return (
    <div className="space-y-6 fade-in" data-testid="production-pack-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/production-packs')}
            className="mb-4 -ml-4"
            data-testid="back-to-packs-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Production Packs
          </Button>
          
          <h1 
            className="text-3xl font-medium text-zinc-900"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {pack.idea_title}
          </h1>
          
          <div className="flex items-center gap-3 mt-3">
            <span className={`text-sm px-3 py-1 rounded-sm ${categoryStyle.bg} ${categoryStyle.text}`}>
              {pack.category}
            </span>
            <span className={`text-sm px-3 py-1 rounded-sm ${
              pack.approval_status === 'Approved' ? 'bg-emerald-50 text-emerald-700' :
              pack.approval_status === 'Pending' ? 'bg-amber-50 text-amber-700' :
              'bg-red-50 text-red-700'
            }`}>
              {pack.approval_status}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <span className={`text-xs uppercase tracking-widest flex items-center gap-1.5 ${saving ? 'text-amber-500' : 'text-zinc-400'}`} data-testid="autosave-indicator">
            {saving ? (
              <><Loader2 className="w-3 h-3 animate-spin" /> Saving...</>
            ) : (
              <><Check className="w-3 h-3" /> Auto-saved</>
            )}
          </span>
          
          {isAdmin() && pack.approval_status === 'Pending' && (
            <Button
              onClick={handleApprove}
              className="bg-[#D4AF37] hover:bg-amber-600 text-white rounded-sm px-6 py-5 text-xs uppercase tracking-widest"
              data-testid="approve-pack-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve
            </Button>
          )}
          
          {pack.approval_status !== 'Completed' && (
            <Button
              onClick={handleMarkCompleted}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 py-5 text-xs uppercase tracking-widest"
              data-testid="mark-completed-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark Completed
            </Button>
          )}
        </div>
      </div>

      {/* Overview Voice Note - between header and tabs */}
      <OverviewVoiceNote 
        packId={id}
        voiceNote={pack.overview_voice_note}
        onUpdate={fetchPack}
      />

      {/* Content Tabs */}
      <Tabs defaultValue={isCustomIP ? "customip" : "script"} className="bg-white border border-zinc-100">
        <TabsList className="w-full justify-start border-b border-zinc-100 bg-transparent p-0 h-auto flex-wrap">
          {isCustomIP ? (
            <TabsTrigger 
              value="customip" 
              className="px-4 md:px-6 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent"
            >
              <Package className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Custom IP</span>
            </TabsTrigger>
          ) : (
            <>
              <TabsTrigger 
                value="script" 
                className="px-4 md:px-6 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent"
              >
                <FileText className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Script</span>
              </TabsTrigger>
              <TabsTrigger 
                value="visuals" 
                className="px-4 md:px-6 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent"
              >
                <Image className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Visuals</span>
                {(rawVisualFiles.length + referenceVisualFiles.length) > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-zinc-100 rounded">
                    {rawVisualFiles.length + referenceVisualFiles.length}
                  </span>
                )}
              </TabsTrigger>
            </>
          )}
          <TabsTrigger 
            value="audio" 
            className="px-4 md:px-6 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent"
          >
            <Music className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Audio</span>
            {audioFiles.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-zinc-100 rounded">{audioFiles.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger 
            value="captions" 
            className="px-4 md:px-6 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Captions</span>
          </TabsTrigger>
          <TabsTrigger 
            value="notes" 
            className="px-4 md:px-6 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent"
          >
            <StickyNote className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Notes</span>
          </TabsTrigger>
          <TabsTrigger 
            value="editor" 
            className="px-4 md:px-6 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent"
          >
            <Pencil className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Editor's Notes</span>
            {(editorInstagramRefs.length + editorVoiceNotes.length) > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                {editorInstagramRefs.length + editorVoiceNotes.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger 
            value="final-assets" 
            className="px-4 md:px-6 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent"
          >
            <Film className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Final Assets</span>
            {(pack.final_assets_media || []).length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded">
                {(pack.final_assets_media || []).length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Custom IP Tab (only for Custom IP production packs) */}
        {isCustomIP && (
          <TabsContent value="customip" className="p-4 md:p-6 space-y-8" data-testid="customip-tab">
            {customIPData ? (
              <>
                {/* Custom IP Sections with upload */}
                {CUSTOM_IP_SECTIONS.map(({ key: sectionKey, label, description }) => {
                  const sectionData = customIPData[sectionKey] || {};
                  const media = sectionData.media || [];
                  return (
                    <div key={sectionKey} className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-zinc-900">{label}</h3>
                          <p className="text-xs text-zinc-500">{description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-400 bg-zinc-100 px-2 py-1 rounded">
                            {media.length} file{media.length !== 1 ? 's' : ''}
                          </span>
                          <input
                            ref={el => customIPFileRefs.current[sectionKey] = el}
                            type="file" accept="image/*,video/*" className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleCustomIPUpload(sectionKey, file);
                              e.target.value = '';
                            }}
                          />
                          <Button size="sm" variant="outline"
                            onClick={() => customIPFileRefs.current[sectionKey]?.click()}
                            disabled={customIPUploading === sectionKey}
                            data-testid={`upload-${sectionKey}-btn`}>
                            {customIPUploading === sectionKey
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <Upload className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      {media.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                          {media.map((item) => {
                            const mediaUrl = customIPPacksAPI.getMediaUrl(customIPData.id, sectionKey, item.id);
                            return (
                              <div key={item.id} className="relative group aspect-square bg-zinc-100 rounded-lg overflow-hidden">
                                {item.is_video ? (
                                  <video src={mediaUrl} className="w-full h-full object-cover" muted playsInline />
                                ) : (
                                  <img src={mediaUrl} alt={item.original_name} className="w-full h-full object-cover" />
                                )}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                  <a href={mediaUrl} download={item.original_name}
                                    className="p-2 bg-white/90 rounded-full text-zinc-700 hover:bg-white mr-2"
                                    onClick={e => e.stopPropagation()}>
                                    <Download className="w-4 h-4" />
                                  </a>
                                  <button onClick={() => handleCustomIPDeleteMedia(sectionKey, item.id)}
                                    className="p-2 bg-white/90 rounded-full text-red-600 hover:bg-white">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                                {item.is_video && (
                                  <div className="absolute bottom-2 left-2">
                                    <span className="px-1.5 py-0.5 bg-black/60 text-white text-xs rounded flex items-center gap-1">
                                      <Video className="w-3 h-3" /> Video
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-zinc-50 rounded-lg text-sm text-zinc-400">
                          No files in this section
                        </div>
                      )}
                      {sectionKey === 'reviews' && (sectionData.text_reviews || []).length > 0 && (
                        <div className="space-y-2">
                          {sectionData.text_reviews.map((review, idx) => (
                            <div key={idx} className="p-3 bg-zinc-50 rounded-lg border border-zinc-100">
                              <p className="text-sm text-zinc-700">{review.text}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Reference Reels */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-zinc-900 flex items-center gap-2">
                        <Instagram className="w-5 h-5 text-pink-500" /> Reference Reels
                      </h3>
                      <p className="text-xs text-zinc-500 mt-1">Instagram references for the editor (max 3)</p>
                    </div>
                    <span className="text-xs text-zinc-400 bg-zinc-100 px-2 py-1 rounded">
                      {(customIPData.reference_reels || []).length}/3
                    </span>
                  </div>
                  {(customIPData.reference_reels || []).length < 3 && (
                    <div className="flex gap-2">
                      <input type="url" value={customIPReelUrl}
                        onChange={(e) => setCustomIPReelUrl(e.target.value)}
                        placeholder="Paste Instagram reel URL..."
                        data-testid="customip-reel-input"
                        className="flex-1 px-3 py-2 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-200 text-sm"
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddCustomIPReel(); }}
                      />
                      <Button onClick={handleAddCustomIPReel} disabled={addingCustomIPReel}
                        className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="customip-add-reel-btn">
                        {addingCustomIPReel ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                        Add
                      </Button>
                    </div>
                  )}
                  {(customIPData.reference_reels || []).length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {(customIPData.reference_reels || []).map((reel) => {
                        let embedUrl = null;
                        try {
                          const urlObj = new URL(reel.url);
                          embedUrl = `https://www.instagram.com${urlObj.pathname}embed/`;
                        } catch {}
                        return (
                          <div key={reel.id} className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
                            <div className="aspect-[9/16] max-h-[300px] bg-zinc-100 relative">
                              {embedUrl ? (
                                <iframe src={embedUrl} className="w-full h-full border-0"
                                  allowFullScreen scrolling="no" title="Reference Reel" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <a href={reel.url} target="_blank" rel="noopener noreferrer"
                                    className="text-pink-500 hover:underline flex items-center gap-2">
                                    <Instagram className="w-5 h-5" /> Open in Instagram
                                  </a>
                                </div>
                              )}
                            </div>
                            <div className="p-3 border-t border-zinc-100 flex items-center justify-between">
                              <a href={reel.url} target="_blank" rel="noopener noreferrer"
                                className="text-sm text-pink-500 hover:underline flex items-center gap-1">
                                <ExternalLink className="w-3 h-3" /> Open
                              </a>
                              <button onClick={() => handleDeleteCustomIPReel(reel.id)}
                                className="p-1.5 text-red-500 hover:text-red-600 hover:bg-red-50 rounded">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-zinc-50 rounded-lg text-sm text-zinc-400">
                      No reference reels added yet
                    </div>
                  )}
                </div>

                {/* Editable Script */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-zinc-900 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-zinc-600" /> Script
                    </h3>
                    <Button size="sm" onClick={handleSaveCustomIPScript} disabled={savingCustomIPScript}
                      className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="save-customip-script-btn">
                      {savingCustomIPScript ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                      Save Script
                    </Button>
                  </div>
                  <Textarea value={customIPScript} onChange={(e) => setCustomIPScript(e.target.value)}
                    placeholder="Write the script for this Custom IP video reel..."
                    rows={8} className="w-full resize-none focus:ring-2 focus:ring-zinc-200" />
                </div>

                {/* Founders Visuals */}
                <div className="space-y-4 pt-4 border-t border-zinc-200">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <h3 className="font-medium text-zinc-900 flex items-center gap-2">
                        <Upload className="w-5 h-5 text-[#D4AF37]" /> Founders Visuals
                      </h3>
                      <p className="text-xs text-zinc-500 mt-1">Additional media uploaded by the founder for the editor</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-400 bg-zinc-100 px-2 py-1 rounded" data-testid="founders-visuals-count">
                        {(customIPData.founders_visuals?.media || []).length} file{(customIPData.founders_visuals?.media || []).length !== 1 ? 's' : ''}
                      </span>
                      {(customIPData.founders_visuals?.media || []).length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            const items = customIPData.founders_visuals?.media || [];
                            if (!items.length) return;
                            toast.info(`Downloading ${items.length} file${items.length > 1 ? 's' : ''}…`);
                            for (const it of items) {
                              try {
                                const url = customIPPacksAPI.getMediaDownloadUrl(customIPData.id, 'founders_visuals', it.id);
                                const r = await fetch(url);
                                if (!r.ok) throw new Error('fetch failed');
                                const blob = await r.blob();
                                const u = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = u;
                                link.download = it.original_name || it.filename || 'file';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(u);
                                // Tiny gap so the browser doesn't choke on rapid bursts
                                await new Promise((res) => setTimeout(res, 250));
                              } catch (err) {
                                console.error('Batch download error', err);
                              }
                            }
                            toast.success('Downloads queued — check your browser');
                          }}
                          data-testid="founders-visuals-download-all-btn"
                        >
                          <Download className="w-4 h-4 mr-2" /> Download all
                        </Button>
                      )}
                    </div>
                  </div>

                  {isFounderUser && (
                    <FoundersVisualsUploader
                      packId={customIPData.id}
                      disabled={customIPUploading === 'founders_visuals'}
                      onUploadComplete={() => fetchCustomIPData(customIPData.id)}
                    />
                  )}

                  {(customIPData.founders_visuals?.media || []).length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {(customIPData.founders_visuals?.media || []).map((item) => (
                        <FoundersVisualItem
                          key={item.id}
                          packId={customIPData.id}
                          item={item}
                          canDelete={isFounderUser}
                          onDelete={(mediaId) => handleCustomIPDeleteMedia('founders_visuals', mediaId)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-zinc-50 rounded-lg text-sm text-zinc-400">
                      No founders visuals uploaded yet
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
              </div>
            )}
          </TabsContent>
        )}

        {/* Script Tab */}
        <TabsContent value="script" className="p-4 md:p-6 space-y-6" data-testid="script-tab">
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold mb-3 block">
              <span className="text-[#D4AF37] mr-2">01</span>
              Hook (First 1-2 seconds)
            </label>
            <Textarea
              value={formData.hook}
              onChange={(e) => setFormData({ ...formData, hook: e.target.value })}
              className="min-h-[100px] border-zinc-200 focus:border-zinc-400"
              placeholder="What grabs attention immediately?"
              data-testid="hook-input"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold mb-3 block">
              <span className="text-[#D4AF37] mr-2">02</span>
              Script / On-screen Text
            </label>
            <Textarea
              value={formData.script}
              onChange={(e) => setFormData({ ...formData, script: e.target.value })}
              className="min-h-[200px] border-zinc-200 focus:border-zinc-400 font-mono text-sm"
              placeholder="Write the full script here..."
              data-testid="script-input"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold mb-3 block">
              <span className="text-[#D4AF37] mr-2">03</span>
              Scene-by-Scene Breakdown
            </label>
            <Textarea
              value={formData.scenes}
              onChange={(e) => setFormData({ ...formData, scenes: e.target.value })}
              className="min-h-[200px] border-zinc-200 focus:border-zinc-400"
              placeholder="Scene 1: ...&#10;Scene 2: ...&#10;Scene 3: ..."
              data-testid="scenes-input"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold mb-3 block">
              <Target className="w-4 h-4 inline-block mr-2" />
              Call to Action
            </label>
            <Textarea
              value={formData.cta}
              onChange={(e) => setFormData({ ...formData, cta: e.target.value })}
              className="min-h-[80px] border-zinc-200 focus:border-zinc-400"
              placeholder="What action should viewers take?"
              data-testid="cta-input"
            />
          </div>
        </TabsContent>

        {/* Visuals Tab - NEW ROBUST IMPLEMENTATION */}
        <TabsContent value="visuals" className="p-4 md:p-6 space-y-8" data-testid="visuals-tab">
          {/* Raw Visuals Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                  <Film className="w-4 h-4 text-[#D4AF37]" />
                  Raw Visuals from Founder
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Upload photos and videos for this production
                </p>
              </div>
              <span className="text-xs text-zinc-400 bg-zinc-100 px-2 py-1 rounded">
                {rawVisualFiles.length} files
              </span>
            </div>

            {/* Upload Component */}
            <VisualUploader 
              packId={id}
              visualType="raw"
              onUploadComplete={fetchPack}
              disabled={false}
            />

            {/* Visual Grid */}
            {rawVisualFiles.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
                {rawVisualFiles.map((visual) => (
                  <VisualItem
                    key={visual.id}
                    packId={id}
                    visual={visual}
                    onDelete={(vId) => setVisualDeleteId(vId)}
                    canDelete={canDeleteFile(visual)}
                  />
                ))}
              </div>
            )}

            {rawVisualFiles.length === 0 && (
              <div className="text-center py-8 bg-zinc-50 border border-dashed border-zinc-200 rounded-lg mt-4">
                <Film className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                <p className="text-sm text-zinc-500">No raw visuals uploaded yet</p>
                <p className="text-xs text-zinc-400 mt-1">Upload images (JPG, PNG, WEBP) or videos (MP4, MOV)</p>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-zinc-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-4 text-zinc-400">Reference Materials</span>
            </div>
          </div>

          {/* Reference Visuals Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-blue-500" />
                  Visual References
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Reference images and inspiration for the content
                </p>
              </div>
              <span className="text-xs text-zinc-400 bg-zinc-100 px-2 py-1 rounded">
                {referenceVisualFiles.length} files
              </span>
            </div>

            {/* Upload Component */}
            <VisualUploader 
              packId={id}
              visualType="reference"
              onUploadComplete={fetchPack}
              disabled={false}
            />

            {/* Visual Grid */}
            {referenceVisualFiles.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
                {referenceVisualFiles.map((visual) => (
                  <VisualItem
                    key={visual.id}
                    packId={id}
                    visual={visual}
                    onDelete={(vId) => setVisualDeleteId(vId)}
                    canDelete={canDeleteFile(visual)}
                  />
                ))}
              </div>
            )}

            {referenceVisualFiles.length === 0 && (
              <div className="text-center py-8 bg-zinc-50 border border-dashed border-zinc-200 rounded-lg mt-4">
                <ImageIcon className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                <p className="text-sm text-zinc-500">No reference visuals uploaded yet</p>
                <p className="text-xs text-zinc-400 mt-1">Upload reference images for inspiration</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Audio Tab */}
        <TabsContent value="audio" className="p-4 md:p-6 space-y-6" data-testid="audio-tab">
          {/* Audio Link Section */}
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold mb-3 block">
              Audio Link (External)
            </label>
            <input
              type="url"
              value={formData.audio_link}
              onChange={(e) => setFormData({ ...formData, audio_link: e.target.value })}
              className="w-full px-4 py-3 border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-400"
              placeholder="Paste audio link here (YouTube, SoundCloud, etc.)..."
              data-testid="audio-link-input"
            />
            {formData.audio_link && (
              <a 
                href={formData.audio_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 text-[#D4AF37] hover:text-amber-600"
              >
                <ExternalLink className="w-4 h-4" />
                Open Audio Link
              </a>
            )}
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-zinc-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-4 text-zinc-400">Or upload audio files</span>
            </div>
          </div>

          {/* Audio Upload Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">
                Uploaded Audio Files
              </label>
              <div>
                <input
                  ref={audioInputRef}
                  type="file"
                  accept="audio/*"
                  onChange={handleAudioUpload}
                  className="hidden"
                  data-testid="audio-file-input"
                />
                <Button
                  onClick={() => audioInputRef.current?.click()}
                  disabled={uploadingAudio}
                  variant="outline"
                  size="sm"
                  className="border-zinc-200"
                  data-testid="upload-audio-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingAudio ? 'Uploading...' : 'Upload Audio'}
                </Button>
              </div>
            </div>

            {/* Audio Files List */}
            {audioFiles.length > 0 ? (
              <div className="space-y-3">
                {audioFiles.map((audio) => (
                  <div 
                    key={audio.id} 
                    className="bg-zinc-50 border border-zinc-100 p-4 rounded-sm"
                    data-testid={`audio-item-${audio.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                          <FileAudio className="w-5 h-5 text-[#D4AF37]" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-zinc-900 truncate text-sm" title={audio.original_name}>
                            {audio.original_name}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-400">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {audio.uploader_name}
                            </span>
                            <span>{formatFileSize(audio.file_size)}</span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDateTime(audio.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex-1 md:max-w-xs">
                        <AudioPlayer packId={id} audioId={audio.id} />
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAudioDownload(audio)}
                          className="border-zinc-200 hover:border-zinc-300"
                          data-testid={`download-btn-${audio.id}`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                        {canDeleteFile(audio) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setAudioDeleteId(audio.id)}
                            className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                            data-testid={`delete-btn-${audio.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-zinc-50 border border-dashed border-zinc-200 rounded-sm">
                <Music className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                <p className="text-sm text-zinc-500">No audio files uploaded yet</p>
                <p className="text-xs text-zinc-400 mt-1">Supported: MP3, WAV, OGG, AAC, M4A, FLAC (max 50MB)</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Captions Tab */}
        <TabsContent value="captions" className="p-4 md:p-6 space-y-6" data-testid="captions-tab">
          {/* Generate Caption Button */}
          <div className="flex items-center justify-between">
            <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">
              Caption Options
            </label>
            <Button
              onClick={handleGenerateCaption}
              disabled={generatingCaption}
              variant="outline"
              size="sm"
              data-testid="generate-caption-btn"
            >
              {generatingCaption ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              {generatingCaption ? 'Generating...' : 'Generate Caption'}
            </Button>
          </div>

          {/* Generation Loading State */}
          {generatingCaption && (
            <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-zinc-400 mx-auto mb-2" />
              <p className="text-sm text-zinc-600">Analyzing your pack content...</p>
              <p className="text-xs text-zinc-400 mt-1">Generating 3 caption options with keyword phrases and hashtags</p>
            </div>
          )}

          {/* Generated Options */}
          {captionOptions && !generatingCaption && (
            <div className="space-y-3">
              <p className="text-xs text-zinc-500">Click "Use This" to add a caption to your pack</p>
              {captionOptions.map((opt, i) => (
                <div key={i} className="border border-zinc-200 rounded-sm overflow-hidden" data-testid={`generated-caption-${i}`}>
                  <div className="px-4 py-2.5 bg-zinc-50 border-b border-zinc-100 flex items-center justify-between">
                    <div>
                      <span className="text-xs font-semibold text-zinc-700">{opt.label}</span>
                      {opt.subtitle && <span className="text-xs text-zinc-400 ml-2">{opt.subtitle}</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm" variant="ghost" className="h-7 text-xs text-zinc-500"
                        onClick={() => { navigator.clipboard.writeText(opt.content); toast.success('Copied'); }}
                      >
                        <Copy className="w-3 h-3 mr-1" /> Copy
                      </Button>
                      <Button
                        size="sm" className="h-7 text-xs bg-zinc-900 text-white hover:bg-zinc-800"
                        onClick={() => addGeneratedCaption(opt.content)}
                        data-testid={`use-caption-${i}`}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Use This
                      </Button>
                    </div>
                  </div>
                  <div className="p-4">
                    <pre className="text-sm text-zinc-700 whitespace-pre-wrap font-sans leading-relaxed">{opt.content}</pre>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Existing Captions */}
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold mb-3 block">
              Saved Captions ({formData.captions.length})
            </label>
            
            <div className="space-y-3 mb-4">
              {formData.captions.map((caption, index) => (
                <div key={index} className="flex gap-3 items-start p-4 bg-zinc-50 rounded-sm">
                  <span className="text-xs text-zinc-400 font-semibold mt-1">#{index + 1}</span>
                  <pre className="flex-1 text-sm text-zinc-700 whitespace-pre-wrap font-sans">{caption}</pre>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => { navigator.clipboard.writeText(caption); toast.success('Copied'); }}
                      className="text-zinc-400 hover:text-zinc-600 p-1"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => removeCaption(index)}
                      className="text-zinc-400 hover:text-red-500 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex gap-3">
              <Textarea
                value={newCaption}
                onChange={(e) => setNewCaption(e.target.value)}
                className="flex-1 min-h-[80px] border-zinc-200"
                placeholder="Write a caption option..."
                data-testid="new-caption-input"
              />
              <Button
                onClick={addCaption}
                className="bg-zinc-900 text-white hover:bg-zinc-800"
                data-testid="add-caption-btn"
              >
                Add
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="p-4 md:p-6" data-testid="notes-tab">
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold mb-3 block">
              Notes & Feedback
            </label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="min-h-[300px] border-zinc-200 focus:border-zinc-400"
              placeholder="Add notes, feedback, or discussion points..."
              data-testid="notes-input"
            />
          </div>
        </TabsContent>

        {/* Editor's Notes Tab */}
        <TabsContent value="editor" className="p-4 md:p-6 space-y-8" data-testid="editor-notes-tab">
          {/* Instagram Style References Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-pink-500" />
                  Editing Style References
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Add Instagram reels as editing style references (max 3)
                </p>
              </div>
              <span className="text-xs text-zinc-400 bg-zinc-100 px-2 py-1 rounded">
                {editorInstagramRefs.length}/3
              </span>
            </div>

            {/* Add Instagram Reference Input */}
            {editorInstagramRefs.length < 3 && (
              <div className="mb-4">
                <AddInstagramReference 
                  packId={id} 
                  onAdd={fetchPack}
                  disabled={editorInstagramRefs.length >= 3}
                />
              </div>
            )}

            {/* Instagram References Grid */}
            {editorInstagramRefs.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {editorInstagramRefs.map((ref) => (
                  <InstagramReference
                    key={ref.id}
                    packId={id}
                    reference={ref}
                    onDelete={fetchPack}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-zinc-50 border border-dashed border-zinc-200 rounded-lg">
                <Instagram className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                <p className="text-sm text-zinc-500">No style references added yet</p>
                <p className="text-xs text-zinc-400 mt-1">Paste Instagram reel URLs above to add references</p>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-zinc-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-4 text-zinc-400">Voice Notes</span>
            </div>
          </div>

          {/* Editor Voice Notes Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                  <Mic className="w-4 h-4 text-[#D4AF37]" />
                  Editor Voice Notes
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Record voice notes with editing instructions or feedback
                </p>
              </div>
              <span className="text-xs text-zinc-400 bg-zinc-100 px-2 py-1 rounded">
                {editorVoiceNotes.length} notes
              </span>
            </div>

            {/* Voice Note Recorder */}
            <div className="mb-4">
              <EditorVoiceNoteRecorder packId={id} onUploadComplete={fetchPack} />
            </div>

            {/* Voice Notes List */}
            {editorVoiceNotes.length > 0 ? (
              <div className="space-y-3">
                {editorVoiceNotes.map((voiceNote) => (
                  <EditorVoiceNotePlayer
                    key={voiceNote.id}
                    packId={id}
                    voiceNote={voiceNote}
                    onDelete={fetchPack}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-zinc-50 border border-dashed border-zinc-200 rounded-lg">
                <Mic className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                <p className="text-sm text-zinc-500">No voice notes yet</p>
                <p className="text-xs text-zinc-400 mt-1">Click the button above to record</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Final Assets Tab */}
        <TabsContent value="final-assets" className="p-4 md:p-6 space-y-6" data-testid="final-assets-tab">
          <div className="flex items-center justify-between">
            <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">
              Final Files ({(pack.final_assets_media || []).length})
            </label>
            <div>
              <input ref={finalAssetInputRef} type="file" multiple accept="image/*,video/*" onChange={handleFinalAssetUpload} className="hidden" />
              <Button
                size="sm" variant="outline"
                onClick={() => finalAssetInputRef.current?.click()}
                disabled={uploadingFinalAsset}
                data-testid="upload-final-asset-btn"
              >
                {uploadingFinalAsset ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload Final Files
              </Button>
            </div>
          </div>

          {/* Live upload progress — flaky Wi-Fi uploads can take many minutes,
              so give the user something to look at instead of a black spinner. */}
          {uploadingFinalAsset && finalAssetProgress && (
            <div className="border border-zinc-200 rounded-lg p-3 bg-zinc-50" data-testid="final-asset-progress">
              <div className="flex items-center justify-between text-xs text-zinc-600 mb-1.5">
                <span className="truncate mr-3">
                  Uploading <span className="font-medium">{finalAssetProgress.name}</span>
                  {finalAssetProgress.total > 1 && (
                    <span className="text-zinc-400"> ({finalAssetProgress.index}/{finalAssetProgress.total})</span>
                  )}
                </span>
                <span className="tabular-nums font-medium">{finalAssetProgress.percent}%</span>
              </div>
              <div className="h-1.5 w-full bg-zinc-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-violet-500 transition-all duration-200"
                  style={{ width: `${finalAssetProgress.percent}%` }}
                />
              </div>
            </div>
          )}

          {(!pack.final_assets_media || pack.final_assets_media.length === 0) ? (
            <div className="text-center py-10 text-zinc-400 text-sm border border-dashed border-zinc-200 rounded-sm">
              <Film className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p>No final assets uploaded yet</p>
              <p className="text-xs mt-1">Upload the finished videos, images, or files</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {pack.final_assets_media.map((item) => {
                const fileUrl = productionPacksAPI.getFinalAssetUrl(id, item.id);
                return (
                  <div key={item.id} className="relative group rounded-lg overflow-hidden border border-zinc-200 bg-zinc-50" data-testid={`final-asset-${item.id}`}>
                    <div className="aspect-square">
                      {item.is_video ? (
                        <video src={fileUrl} className="w-full h-full object-cover" muted playsInline />
                      ) : (
                        <img src={fileUrl} alt={item.original_name} className="w-full h-full object-cover" loading="lazy" />
                      )}
                      {item.is_video && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
                            <Play size={18} className="text-white ml-0.5" />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 gap-2">
                      <button
                        onClick={() => setPreviewFinalAsset(item)}
                        className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center text-zinc-700 hover:bg-white"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleDownloadFinalAsset(item.id, item.original_name)}
                        className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center text-zinc-700 hover:bg-white"
                        data-testid={`download-final-${item.id}`}
                      >
                        <Download size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteFinalAsset(item.id)}
                        className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center text-red-500 hover:bg-white"
                        data-testid={`delete-final-${item.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="px-2 py-1.5 bg-white border-t border-zinc-100">
                      <p className="text-xs text-zinc-600 truncate">{item.original_name}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Final Asset Preview Dialog */}
        {previewFinalAsset && (
          <Dialog open={!!previewFinalAsset} onOpenChange={() => setPreviewFinalAsset(null)}>
            <DialogContent className="sm:max-w-3xl bg-white max-h-[90vh]">
              <div className="text-sm font-medium text-zinc-700 mb-2">{previewFinalAsset.original_name}</div>
              <div className="flex items-center justify-center max-h-[70vh] overflow-auto">
                {previewFinalAsset.is_video ? (
                  <video src={productionPacksAPI.getFinalAssetUrl(id, previewFinalAsset.id)} controls autoPlay className="max-w-full max-h-[65vh] rounded" />
                ) : (
                  <img src={productionPacksAPI.getFinalAssetUrl(id, previewFinalAsset.id)} alt={previewFinalAsset.original_name} className="max-w-full max-h-[65vh] rounded object-contain" />
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => handleDownloadFinalAsset(previewFinalAsset.id, previewFinalAsset.original_name)}>
                  <Download size={14} className="mr-1.5" /> Download
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

      </Tabs>

      {/* Meta info */}
      <div className="bg-white border border-zinc-100 p-4 md:p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">Created</label>
            <p className="text-zinc-700 mt-1">{formatDate(pack.created_at)}</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">Last Updated</label>
            <p className="text-zinc-700 mt-1">{formatDate(pack.updated_at)}</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">Linked Idea</label>
            <Link to="/ideas" className="text-[#D4AF37] hover:text-amber-600 mt-1 block">
              View Idea
            </Link>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">Status</label>
            <p className="text-zinc-700 mt-1">{pack.approval_status}</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">Post Type</label>
            <Select
              value={pack.post_type || ''}
              onValueChange={async (value) => {
                try {
                  await productionPacksAPI.update(pack.id, { post_type: value });
                  setPack({ ...pack, post_type: value });
                  toast.success('Post type updated');
                } catch { toast.error('Failed to update'); }
              }}
            >
              <SelectTrigger className="mt-1 w-full" data-testid="pack-post-type-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Carousel">Carousel</SelectItem>
                <SelectItem value="Reel">Reel</SelectItem>
                <SelectItem value="Static">Static</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Audio Delete Confirmation Dialog */}
      <AlertDialog open={!!audioDeleteId} onOpenChange={(open) => !open && setAudioDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Audio File</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this audio file? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAudioDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-audio-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Visual Delete Confirmation Dialog */}
      <AlertDialog open={!!visualDeleteId} onOpenChange={(open) => !open && setVisualDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Visual File</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this file? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleVisualDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-visual-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductionPackDetail;
